import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Upload, Mountain, LogOut, User, TrendingUp, Activity, Clock, Trash2, Pencil, CalendarDays, MapPin, KeyRound, Share2, Smartphone, CheckCircle2, Image, Building2, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RouteMap from '@/components/RouteMap';
import RuggedTopNav from '@/components/RuggedTopNav';
import ElevationChart from '@/components/ElevationChart';
// NavigationMode removido do web (Ago/2026 — Ricardo): GPS turn-by-turn só faz sentido no mobile.
import RouteRatings from '@/components/RouteRatings';
import UsersManager from '@/components/UsersManager';
// Fev/2026 — Code-splitting agressivo dos modais admin que carregam pesado
// (LiveKit, Leaflet extra, gráficos, moderação). Carregam apenas quando
// abertos, cortando ~1.2 MB do bundle inicial e passando a primeira
// renderização de ~3-4s para ~800ms em desktop fibra.
const MunicipalitiesManager = lazy(() => import('@/components/MunicipalitiesManager'));
const GroupsManager = lazy(() => import('@/components/GroupsManager'));
const PasseiosOrganizados = lazy(() => import('@/components/PasseiosOrganizados'));
const CommunityPanel = lazy(() => import('@/components/community/CommunityPanel').then((m) => ({ default: m.CommunityPanel })));
const NavChallengeManager = lazy(() => import('@/components/NavChallengeManager'));
const ResetRequestsManager = lazy(() => import('@/components/ResetRequestsManager'));
const OemKeysManager = lazy(() => import('@/components/OemKeysManager'));
const SOSLiveDashboard = lazy(() => import('@/components/SOSLiveDashboard'));
const ReportsModerationPanel = lazy(() => import('@/components/ReportsModerationPanel'));
const MetricsPanel = lazy(() => import('@/components/MetricsPanel'));
const BroadcastPanel = lazy(() => import('@/components/BroadcastPanel'));
const WebWalkie = lazy(() => import('@/components/WebWalkie'));
const HistoryViewer = lazy(() => import('@/components/HistoryViewer'));
const AutocolantesPanel = lazy(() => import('@/components/AutocolantesPanel'));
const SupportMessagesPanel = lazy(() => import('@/components/SupportMessagesPanel'));
const DraftsPanel = lazy(() => import('@/components/DraftsPanel'));
const ExportReport = lazy(() => import('@/components/ExportReport'));
const CompaniesManager = lazy(() => import('@/components/CompaniesManager'));
import POIManager from '@/components/POIManager';
import RoutePhotosGallery from '@/components/RoutePhotosGallery';
import WebBillingGuard from '@/components/WebBillingGuard';
import ContactForm from '@/components/ContactForm';
import TermsModal from '@/components/TermsModal';
import { RotaDoMesWeb } from '@/components/RotaDoMesWeb';
import ErrorConsole from '@/components/ErrorConsole';
import SOSAlertsBanner from '@/components/SOSAlertsBanner';
import RankingCardModal from '@/components/RankingCardModal';
import SocialCardsModal from '@/components/SocialCardsModal';
import RouteMunicipalitiesModal from '@/components/RouteMunicipalitiesModal';
import UploadRouteDialog from '@/components/UploadRouteDialog';
import EditRouteDialog from '@/components/EditRouteDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, setUser } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  // POI focado — quando o user clica num POI da lista, RouteMap faz flyTo.
  // Guarda-se { id, latitude, longitude, _t } — _t é timestamp para re-triggar
  // o useEffect mesmo quando se clica no mesmo POI duas vezes.
  const [focusPOI, setFocusPOI] = useState(null);
  // Fev/2026 (v20) — botão "Expandir" nas tabs POIs/Fotos abre um Dialog
  // grande (max-w-6xl) com o mesmo componente em versão maior. Útil para
  // edição de rotas com dezenas de POIs ou galerias grandes que ficam
  // apertadas no painel lateral (max-h-30vh original).
  const [expandedPanel, setExpandedPanel] = useState(null); // null | 'pois' | 'photos'
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState([]); // multi-select: ['suv-4x4','moto',...]
  const [municipioFilter, setMunicipioFilter] = useState('all');
  // Município / empresa: mostrar só as rotas do próprio concelho ou da própria empresa.
  const [onlyMine, setOnlyMine] = useState(false); // Fev/2026 — filtrar rotas por município seleccionado
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [rankingOpen, setRankingOpen] = useState(false);
  const [companiesOpen, setCompaniesOpen] = useState(false);
  const [municipiosOpen, setMunicipiosOpen] = useState(false);
  // Rota alvo do modal Social Cards (aberto a partir do botão "CARD"
  // em qualquer rota da lista — não apenas rascunhos).
  const [socialCardsFor, setSocialCardsFor] = useState(null);
  // Rota alvo do modal Municípios (associação many-to-many de rotas ↔ concelhos).
  const [muniModalFor, setMuniModalFor] = useState(null);
  // Nome/dados dos concelhos (para renderizar pills no route card). Carregado
  // uma vez após auth. Map slug → {name, district}.
  const [muniMap, setMuniMap] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [uploadData, setUploadData] = useState({ name: '', description: '', file: null, coverImage: null, difficulty: '', route_type: '', region: '', tags: '', group_id: '', group_ids: [], is_private: false, vehicle_badges: [], logos: [], photos: [], company_id: '', municipality_ids: [] });
  const [uploading, setUploading] = useState(false);
  // Fev/2026 (Ricardo) — estado real do cálculo BRouter após upload.
  // null = inactivo | {state:'computing'} | {state:'done', count, dist_m, asc_m} | {state:'failed', error}
  const [turnsStatus, setTurnsStatus] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  // isNavigating removido (Ago/2026): navegação GPS turn-by-turn só existe no mobile.
  // Termos & Condições — guardado na conta (terms_accepted_at); localStorage antigo é sincronizado.
  const TERMS_KEY = (uid) => `tectur_terms_accepted_${uid}`;
  const [termsAccepted, setTermsAccepted] = useState(true); // optimistic; calculado no useEffect
  const [toolbarMore, setToolbarMore] = useState(false);
  const saveTermsOnServer = () => axios.post(`${API}/auth/accept-terms`, {}, { withCredentials: true }).catch(() => {});
  useEffect(() => {
    if (!user?.user_id) return;
    if (user.terms_accepted_at) return setTermsAccepted(true);
    const local = !!localStorage.getItem(TERMS_KEY(user.user_id));
    if (local) saveTermsOnServer();
    setTermsAccepted(local);
  }, [user?.user_id, user?.terms_accepted_at]);
  const acceptTerms = () => {
    if (user?.user_id) localStorage.setItem(TERMS_KEY(user.user_id), new Date().toISOString());
    saveTermsOnServer();
    setTermsAccepted(true);
  };
  const logoutFromTerms = async () => {
    try { await logout(); } finally { navigate('/login'); }
  };
  // routeCoordinates removido (Ago/2026): usado apenas pelo NavigationMode.
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({ route_id: '', name: '', description: '', difficulty: '', route_type: '', group_ids: [], is_private: false, vehicle_badges: [], newLogos: [], newPhotos: [] });
  const [editSaving, setEditSaving] = useState(false);
  // Recarregar GPX/KML — substitui o ficheiro de uma rota EXISTENTE mantendo
  // reviews/comentários/ratings/downloads históricos. Apenas admin.
  const [reloadFile, setReloadFile] = useState(null);
  const [reloadSaving, setReloadSaving] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdData, setPwdData] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  // Track browser online/offline state for download button
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      if (location.state?.user) {
        setUser(location.state.user);
        setIsAuthenticated(true);
        // Municípios/empresas ficam no principal com mapa; o painel próprio abre no botão do topo.
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    if (isAuthenticated === null) {
      checkAuth();
    }
  }, [isAuthenticated, location.state, navigate, setUser]);

  // Load routes
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await axios.get(`${API}/routes`);
        setRoutes(response.data);
        setFilteredRoutes(response.data);
        if (response.data.length > 0) {
          setSelectedRoute((cur) => cur || response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching routes:', error);
        toast.error('Erro ao carregar rotas');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchRoutes();
      // Municípios: carrega uma vez (só admins precisam para pills)
      if ((user?.role === 'admin' || user?.municipality_id) && Object.keys(muniMap).length === 0) {
        axios.get(`${API}/municipalities`)
          .then((r) => {
            const map = {};
            (r.data || []).forEach((m) => { map[m.municipality_id] = m; });
            setMuniMap(map);
          })
          .catch(() => {});
      }
      // Error Monitor: pré-carrega contador de erros não-resolvidos para a badge.
      // Apenas admins conseguem, os outros recebem 403 (ignorado silenciosamente).
      if (user?.role === 'admin') {
        axios.get(`${API}/admin/errors/stats`, { withCredentials: true })
          .then((r) => setErrorCount(r.data?.unresolved || 0))
          .catch(() => {});
      }
    }
  }, [isAuthenticated, user?.role]);

  // Nome da empresa para o filtro "Só rotas de <empresa>".
  useEffect(() => {
    if (!isAuthenticated || !user?.company_id || user?.role === 'admin') return;
    axios.get(`${API}/companies/${user.company_id}`, { withCredentials: true })
      .then((r) => setCompanyName(r.data?.name || ''))
      .catch(() => {});
  }, [isAuthenticated, user?.company_id, user?.role]);

  // Apply client-side filters: search, difficulty and category
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = routes.filter((r) => {
      // Esconde rascunhos da lista principal — admin gere-os no painel Rascunhos
      if (r.is_draft) return false;
      // Fix A (Fev/2026): rotas de empresa NÃO aparecem no dashboard admin geral.
      // Só ficam visíveis no CompanyDashboard (`/empresa?company_id=X`).
      // Isto isola o admin global das rotas B2B (elas continuam a aparecer para
      // o marshall/staff/user da empresa via /api/routes que já faz o scope).
      if (user?.role === 'admin' && r.company_id) return false;
      // Fev/2026 — Rotas atribuídas a municípios saem da lista do dashboard admin
      // e passam a ser geridas dentro do painel do município. No mobile e no
      // `/m/:slug` público continuam a aparecer normalmente. Isto compartimenta
      // o admin e evita a lista global crescer sem controlo.
      // EXCEPÇÃO: se o admin escolher um município no dropdown de filtro, só
      // aparecem as rotas atribuídas a esse município (fluxo de gestão pontual).
      if (user?.role === 'admin') {
        const mids = Array.isArray(r.municipality_ids) ? r.municipality_ids : [];
        if (municipioFilter === 'all') {
          if (mids.length > 0) return false;
        } else {
          if (!mids.includes(municipioFilter)) return false;
        }
      }
      if (onlyMine && user?.role !== 'admin') {
        if (user?.role === 'municipio' && user?.municipality_id) {
          const mids = Array.isArray(r.municipality_ids) ? r.municipality_ids : [];
          if (!mids.includes(user.municipality_id)) return false;
        } else if (user?.company_id && r.company_id !== user.company_id) {
          return false;
        }
      }
      if (q) {
        const hay = `${r.name || ''} ${r.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (difficultyFilter !== 'all' && r.difficulty !== difficultyFilter) return false;
      // Categoria multi-select: cada opção escolhida DEVE estar presente (AND)
      // 'suv-4x4' | '4x4-reductoras'  → route_type
      // 'car' | 'moto'                → vehicle_badges (lista)
      if (Array.isArray(typeFilter) && typeFilter.length > 0) {
        for (const opt of typeFilter) {
          if (opt === 'car' || opt === 'moto') {
            if (!(r.vehicle_badges || []).includes(opt)) return false;
          } else if (r.route_type !== opt) {
            return false;
          }
        }
      }
      return true;
    });
    setFilteredRoutes(filtered);
    // Filtro "só as minhas rotas": se a rota aberta saiu da lista, abre a primeira que ficou.
    if (onlyMine) {
      setSelectedRoute((cur) => (cur && filtered.some((r) => r.route_id === cur.route_id) ? cur : (filtered[0] || null)));
    }
  }, [routes, searchQuery, difficultyFilter, typeFilter, user?.role, municipioFilter, onlyMine, user?.municipality_id, user?.company_id]);

  // Pré-carrega grupos para resolver nomes nos badges "🔒 Privado: Nome"
  // (admin pode estar a navegar a lista sem ainda ter aberto upload/edit).
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') return;
    axios.get(`${API}/groups`, { withCredentials: true })
      .then((res) => setAvailableGroupsForEdit(res.data || []))
      .catch(() => {});
  }, [isAuthenticated, user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    // Validação explícita com toast visível + log — Fev/2026 (Ricardo):
    // antes falhávamos silenciosamente por `required` HTML sem feedback.
    if (!uploadData.file) {
      console.warn('[handleUpload] bloqueado: ficheiro GPX/KML em falta');
      toast.error('Falta seleccionar o ficheiro GPX ou KML');
      return;
    }
    if (!uploadData.name || !uploadData.name.trim()) {
      console.warn('[handleUpload] bloqueado: nome em falta');
      toast.error('Falta preencher o nome da rota');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      if (uploadData.coverImage) {
        formData.append('cover_image', uploadData.coverImage);
      }

      const params = new URLSearchParams({
        name: uploadData.name,
        ...(uploadData.description && { description: uploadData.description }),
        ...(uploadData.difficulty && { difficulty: uploadData.difficulty }),
        ...(uploadData.route_type && { route_type: uploadData.route_type }),
        ...(uploadData.region && { region: uploadData.region }),
        ...(uploadData.tags && { tags: uploadData.tags }),
        ...(uploadData.group_id && { group_id: uploadData.group_id }),
        ...(uploadData.vehicle_badges && uploadData.vehicle_badges.length > 0 && { vehicle_badges: uploadData.vehicle_badges.join(',') }),
        ...(uploadData.draft && { notify_on_publish: 'false' }),
        // Fev/2026 (Ricardo) — associação directa a empresa/município no upload
        ...(uploadData.company_id && { company_id: uploadData.company_id }),
        ...(Array.isArray(uploadData.municipality_ids) && uploadData.municipality_ids.length > 0 && {
          municipality_ids: uploadData.municipality_ids.join(','),
        }),
      });

      // ⚠️ NÃO definir Content-Type manualmente — axios 1.x não gera o
      // `boundary=...` necessário e FastAPI responde 400. Browser gera-o
      // automaticamente quando o body é FormData. (fix Fev/2026 Ricardo).
      const response = await axios.post(
        `${API}/routes/upload?${params.toString()}`,
        formData,
        { withCredentials: true }
      );

      // Segunda chamada — upload dos logos (se houver). Não bloqueia a rota
      // caso falhe: a rota já foi criada e o admin pode voltar a tentar em Edit.
      const created = response.data;
      const logoFiles = Array.isArray(uploadData.logos) ? uploadData.logos : [];
      if (created?.route_id && logoFiles.length > 0) {
        try {
          const logoFd = new FormData();
          logoFiles.forEach((f) => logoFd.append('files', f));
          await axios.post(`${API}/routes/${created.route_id}/logos`, logoFd, {
            withCredentials: true,
          });
          created.logo_count = logoFiles.length;
        } catch (err) {
          toast.warning('Rota criada mas falhou o upload de logos — tenta novamente em Editar.');
        }
      }

      // Terceira chamada — upload das fotos da galeria (se houver). Mesma
      // estratégia dos logos: não bloqueia se falhar.
      const photoFiles = Array.isArray(uploadData.photos) ? uploadData.photos : [];
      if (created?.route_id && photoFiles.length > 0) {
        try {
          const photoFd = new FormData();
          photoFiles.forEach((f) => photoFd.append('files', f));
          await axios.post(`${API}/routes/${created.route_id}/photos`, photoFd, {
            withCredentials: true,
          });
          created.photo_count = photoFiles.length;
        } catch (err) {
          toast.warning('Rota criada mas falhou o upload de fotos — tenta novamente em Editar.');
        }
      }

      setRoutes([created, ...routes]);
      setFilteredRoutes([created, ...filteredRoutes]);
      setSelectedRoute(created);

      // Fev/2026 (Ricardo) — mantem modal aberto a mostrar progresso BRouter real.
      // Polling de /turns a cada 1.5s até detectar `computed_at` ou timeout 45s.
      setTurnsStatus({ state: 'computing' });
      const started = Date.now();
      let settled = false;
      // Se o backend já devolveu turns_summary.ok=true no upload (comum < 3s),
      // mostramos logo o estado final sem precisar de poll.
      if (created?.turns_summary?.ok) {
        settled = true;
        setTurnsStatus({
          state: 'done',
          count: created.turns_summary.turns_count,
          dist_m: created.turns_summary.dist_m,
          asc_m: created.turns_summary.asc_m,
          profile: created.turns_summary.profile,
        });
      } else if (created?.turns_summary && created.turns_summary.ok === false) {
        settled = true;
        setTurnsStatus({ state: 'failed', error: created.turns_summary.error || 'BRouter devolveu vazio' });
      }
      while (!settled && Date.now() - started < 45000) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const t = await axios.get(`${API}/routes/${created.route_id}/turns`, { withCredentials: true });
          if (t?.data?.computed_at) {
            setTurnsStatus({
              state: 'done',
              count: (t.data.turns || []).length,
              dist_m: t.data.dist_m,
              asc_m: t.data.asc_m,
              profile: t.data.profile,
            });
            settled = true;
          }
        } catch { /* ignora, tenta de novo */ }
      }
      if (!settled) {
        setTurnsStatus({ state: 'failed', error: 'Timeout (45s) — verifica o BRouter daemon no servidor' });
      }

      setUploadData({ name: '', description: '', file: null, coverImage: null, difficulty: '', route_type: '', region: '', tags: '', group_id: '', group_ids: [], is_private: false, vehicle_badges: [], logos: [], photos: [], company_id: '', municipality_ids: [] });
      // NOTA: modal NÃO é fechado automaticamente — fica a mostrar o
      // resultado do BRouter até o utilizador clicar "Fechar".
      toast.success('Rota carregada com sucesso!');

      // Resumo BRouter (turns) — mostra ao admin o que foi calculado.
      const ts = created?.turns_summary;
      if (ts?.ok) {
        toast.success(
          `Rota criada ✓ · ${ts.turns_count} curvas calculadas ` +
          `(perfil ${ts.profile}, ${(ts.dist_m/1000).toFixed(1)} km, ` +
          `↑${ts.asc_m}m / ↓${ts.dsc_m}m)`,
          { duration: 6000 }
        );
      } else if (ts && !ts.ok) {
        toast.error(
          `Rota criada mas turns FALHARAM após ${ts.attempts} tentativa(s): ${ts.error}. ` +
          `Podes recalcular em "Editar → Re-enriquecer".`,
          { duration: 10000 }
        );
      }
      // Aplicar visibilidade (público/privado + grupos) via PATCH — o endpoint
      // /routes/upload só aceita 1 grupo. Se o admin escolheu vários grupos ou
      // marcou como PRIVADA sem grupo, PATCH garante que fica coerente.
      if (created?.route_id) {
        const wantGroups = Array.isArray(uploadData.group_ids) ? uploadData.group_ids : [];
        const wantPrivate = !!uploadData.is_private;
        if (wantGroups.length > 0 || wantPrivate) {
          try {
            const r = await axios.patch(
              `${API}/routes/${created.route_id}`,
              { is_private: wantPrivate, group_ids: wantGroups },
              { withCredentials: true },
            );
            const updated = r.data;
            setRoutes((prev) => prev.map((rt) => rt.route_id === created.route_id ? { ...rt, ...updated } : rt));
            setFilteredRoutes((prev) => prev.map((rt) => rt.route_id === created.route_id ? { ...rt, ...updated } : rt));
            setSelectedRoute((prev) => prev?.route_id === created.route_id ? { ...prev, ...updated } : prev);
          } catch (err) {
            toast.warning('Rota criada mas falhou a atualização de visibilidade — configura em Editar.');
          }
        }
      }
      setUploadData({ name: '', description: '', file: null, coverImage: null, difficulty: '', route_type: '', region: '', tags: '', group_id: '', group_ids: [], is_private: false, vehicle_badges: [], logos: [], photos: [] });
      setUploadOpen(false);
      toast.success('Rota carregada com sucesso!');
    } catch (error) {
      // Fev/2026 (Ricardo) — diagnóstico detalhado do upload que falhava
      // silenciosamente: log completo + toast com causa real (status +
      // detail do backend, ou mensagem de rede se o Service Worker rebenta).
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const dataStr = (() => {
        try { return JSON.stringify(error?.response?.data); } catch { return String(error?.response?.data); }
      })();
      console.error('[handleUpload] FALHA upload rota', {
        status,
        detail,
        data: error?.response?.data,
        message: error?.message,
        code: error?.code,
        url: error?.config?.url,
        stack: error?.stack,
      });
      let msg;
      if (status) {
        msg = `Erro ${status}: ${detail || dataStr || 'sem detalhe do backend'}`;
      } else if (error?.message) {
        msg = `Erro de rede: ${error.message}${error?.code ? ' (' + error.code + ')' : ''}`;
      } else {
        msg = 'Erro desconhecido ao carregar rota — ver consola F12';
      }
      toast.error(msg, { duration: 10000 });
      // Fecha o modal mesmo em erro para o user ver o toast — v41 (Ricardo).
      // Antes ficava preso e o utilizador pensava que o botão não fazia nada.
      setUploadOpen(false);
    } finally {
      setUploading(false);
    }
  };

  // 📲 Guardar rota no MEU telemóvel — mudança arquitectural (Jul/2026):
  // agora cada utilizador escolhe quais as rotas que quer guardar offline
  // no SEU dispositivo (em vez do admin fazer broadcast a todos).
  const [myDownloadIds, setMyDownloadIds] = useState(new Set());

  const isInMyDownloads = (routeId) => myDownloadIds.has(routeId);

  // Carrega a lista de rotas que este user marcou para o seu telemóvel
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/my/downloads`, { withCredentials: true });
        setMyDownloadIds(new Set(Array.isArray(res.data) ? res.data : []));
      } catch {
        /* silent — não bloqueia UI */
      }
    })();
  }, [isAuthenticated]);

  const handleAddToMyDownloads = async (route) => {
    if (!isOnline) {
      toast.error('Sem ligação à Internet');
      return;
    }
    try {
      await axios.post(`${API}/my/downloads/${route.route_id}`, {}, { withCredentials: true });
      setMyDownloadIds((prev) => new Set(prev).add(route.route_id));
      toast.success('Guardado no teu telemóvel 📲', {
        description: 'A app vai descarregar esta rota automaticamente ao abrires.',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar rota');
    }
  };

  const handleRemoveFromMyDownloads = async (route) => {
    try {
      await axios.delete(`${API}/my/downloads/${route.route_id}`, { withCredentials: true });
      setMyDownloadIds((prev) => {
        const s = new Set(prev);
        s.delete(route.route_id);
        return s;
      });
      toast.success('Removido do teu telemóvel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover');
    }
  };

  // Recarrega a rota selecionada do servidor (após editar POIs).
  const refreshSelectedRoute = async () => {
    if (!selectedRoute) return;
    try {
      const res = await axios.get(`${API}/routes/${selectedRoute.route_id}`, { withCredentials: true });
      const updated = res.data;
      setRoutes((prev) => prev.map((r) => (r.route_id === updated.route_id ? updated : r)));
      setSelectedRoute(updated);
    } catch { /* noop */ }
  };

  // Sincroniza um patch parcial para as 3 listas de rotas (routes, filteredRoutes, selectedRoute).
  // Usado por EditRouteDialog para propagar alterações in-place (capa, logos, fotos, etc.).
  const patchRoute = (routeId, patch) => {
    setRoutes((prev) => prev.map((rt) => rt.route_id === routeId ? { ...rt, ...patch } : rt));
    setFilteredRoutes((prev) => prev.map((rt) => rt.route_id === routeId ? { ...rt, ...patch } : rt));
    setSelectedRoute((prev) => prev?.route_id === routeId ? { ...prev, ...patch } : prev);
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta rota?')) return;

    try {
      await axios.delete(`${API}/routes/${routeId}`, { withCredentials: true });
      setRoutes(routes.filter((r) => r.route_id !== routeId));
      setFilteredRoutes(filteredRoutes.filter((r) => r.route_id !== routeId));
      if (selectedRoute?.route_id === routeId) {
        setSelectedRoute(filteredRoutes[0] || null);
      }
      toast.success('Rota eliminada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar rota');
    }
  };

  const [availableGroupsForEdit, setAvailableGroupsForEdit] = useState([]);
  // Fev/2026 (Ricardo) — admin pode associar logo a empresa/município no upload
  const [availableCompaniesForUpload, setAvailableCompaniesForUpload] = useState([]);
  const [availableMunisForUpload, setAvailableMunisForUpload] = useState([]);
  const openEdit = (route) => {
    setEditData({
      route_id: route.route_id,
      name: route.name || '',
      description: route.description || '',
      difficulty: route.difficulty || '',
      route_type: route.route_type || '',
      group_ids: route.group_ids || [],
      vehicle_badges: route.vehicle_badges || [],
      // is_private: usa o valor explícito do backend; fallback legacy = tem grupos
      is_private: typeof route.is_private === 'boolean'
        ? route.is_private
        : (route.group_ids || []).length > 0,
      // Contagens actuais para renderizar thumbnails de logos/fotos existentes
      logo_count: route.logo_count || 0,
      photo_count: route.photo_count || 0,
      // Foto de capa — o backend expõe `cover_image_path` (string) OU
      // `has_cover` (bool, para respostas de list mais leves). Aceitamos ambos.
      has_cover: !!(route.cover_image_path || route.has_cover),
      _coverV: Date.now(),
      newLogos: [],
      newPhotos: [],
    });
    // Pré-carrega grupos disponíveis (admin)
    if (user?.role === 'admin') {
      axios.get(`${API}/groups`, { withCredentials: true })
        .then((res) => setAvailableGroupsForEdit(res.data || []))
        .catch(() => setAvailableGroupsForEdit([]));
    }
    setEditOpen(true);
  };

  // Limpar ficheiro escolhido para reload quando o diálogo fecha
  useEffect(() => {
    if (!editOpen) setReloadFile(null);
  }, [editOpen]);

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editData.name.trim()) {
      toast.error('O nome não pode estar vazio');
      return;
    }
    setEditSaving(true);
    try {
      const payload = {
        name: editData.name.trim(),
        description: editData.description,
        difficulty: editData.difficulty || null,
        route_type: editData.route_type || null,
        group_ids: Array.isArray(editData.group_ids) ? editData.group_ids : [],
        is_private: !!editData.is_private,
        vehicle_badges: Array.isArray(editData.vehicle_badges) ? editData.vehicle_badges : [],
      };
      const res = await axios.patch(
        `${API}/routes/${editData.route_id}`,
        payload,
        { withCredentials: true }
      );
      let updated = res.data;

      // Uploads adicionais — anexar novos logos/fotos se existirem no editData.
      // Se algum falhar, mostramos aviso mas não bloqueamos a edição.
      const newLogos = Array.isArray(editData.newLogos) ? editData.newLogos : [];
      if (newLogos.length > 0) {
        try {
          const fd = new FormData();
          newLogos.forEach((f) => fd.append('files', f));
          const r = await axios.post(`${API}/routes/${editData.route_id}/logos`, fd, {
            withCredentials: true,
          });
          updated = { ...updated, logo_count: r.data.logo_count };
        } catch (err) {
          toast.warning(err.response?.data?.detail || 'Falha a adicionar logos');
        }
      }
      const newPhotos = Array.isArray(editData.newPhotos) ? editData.newPhotos : [];
      if (newPhotos.length > 0) {
        try {
          const fd = new FormData();
          newPhotos.forEach((f) => fd.append('files', f));
          const r = await axios.post(`${API}/routes/${editData.route_id}/photos`, fd, {
            withCredentials: true,
          });
          updated = { ...updated, photo_count: r.data.photo_count };
        } catch (err) {
          toast.warning(err.response?.data?.detail || 'Falha a adicionar fotos');
        }
      }

      setRoutes((prev) => prev.map((r) => (r.route_id === updated.route_id ? updated : r)));
      setFilteredRoutes((prev) => prev.map((r) => (r.route_id === updated.route_id ? updated : r)));
      if (selectedRoute?.route_id === updated.route_id) setSelectedRoute(updated);
      toast.success('Rota atualizada');
      setEditOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar rota');
    } finally {
      setEditSaving(false);
    }
  };

  // Recarregar nova versão GPX/KML para uma rota existente.
  // Mantém route_id + reviews/ratings/comentários/downloads históricos.
  const handleReloadContent = async () => {
    if (!reloadFile) {
      toast.error('Escolha primeiro um ficheiro GPX ou KML');
      return;
    }
    const name = (reloadFile.name || '').toLowerCase();
    if (!name.endsWith('.gpx') && !name.endsWith('.kml')) {
      toast.error('Apenas ficheiros .gpx ou .kml são aceites');
      return;
    }
    const ok = window.confirm(
      'Substituir o ficheiro desta rota por esta nova versão?\n\n' +
      '• O route_id mantém-se (links partilhados continuam a funcionar)\n' +
      '• Reviews, comentários, ratings e downloads históricos são preservados\n' +
      '• Estatísticas (km, elevação, duração, perfil, POIs) são recalculadas\n\n' +
      'Continuar?'
    );
    if (!ok) return;
    setReloadSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', reloadFile);
      const res = await axios.put(
        `${API}/routes/${editData.route_id}/content`,
        formData,
        { withCredentials: true }
      );
      const updated = res.data;
      setRoutes((prev) => prev.map((r) => (r.route_id === updated.route_id ? updated : r)));
      setFilteredRoutes((prev) => prev.map((r) => (r.route_id === updated.route_id ? updated : r)));
      if (selectedRoute?.route_id === updated.route_id) setSelectedRoute(updated);
      setReloadFile(null);
      const ts = updated?.turns_summary;
      if (ts?.ok) {
        toast.success(
          `Nova versão carregada ✓ · reviews preservados · ` +
          `${ts.turns_count} curvas re-calculadas (perfil ${ts.profile}, ` +
          `v${ts.polv}, ${(ts.dist_m/1000).toFixed(1)} km)`,
          { duration: 6000 }
        );
      } else if (ts && !ts.ok) {
        toast.error(
          `Nova versão carregada mas TURNS FALHARAM: ${ts.error}. ` +
          `Usa "Re-enriquecer" para tentar novamente.`,
          { duration: 10000 }
        );
      } else {
        toast.success('Nova versão carregada — reviews e comentários preservados ✅');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao recarregar GPX/KML');
    } finally {
      setReloadSaving(false);
    }
  };

  const formatUploadDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (!pwdData.current || !pwdData.next || !pwdData.confirm) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (pwdData.next !== pwdData.confirm) {
      toast.error('A confirmação não corresponde à nova palavra-passe');
      return;
    }
    if (pwdData.next.length < 8) {
      toast.error('A nova palavra-passe tem de ter pelo menos 8 caracteres');
      return;
    }
    setPwdSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwdData.current, new_password: pwdData.next },
        { withCredentials: true },
      );
      toast.success('Palavra-passe alterada com sucesso');
      setPwdOpen(false);
      setPwdData({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao alterar palavra-passe');
    } finally {
      setPwdSaving(false);
    }
  };

  // handleStartNavigation removido (Ago/2026 — Ricardo): a navegação GPS
  // turn-by-turn não faz sentido em desktop; feature vive só no mobile app.

  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">A carregar...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#8A7A63' }}>A carregar…</div>}>
    <div className="min-h-screen flex flex-col pb-20 md:pb-0 md:h-screen md:overflow-hidden" data-testid="dashboard">
      {/* Top nav Rugged (mock-admin-dashboard-rugged.html) — clique em tab
          dispara o botão correspondente no sidebar toolbar via testid.
          Isto mantém 100% da funcionalidade actual sem duplicar código. */}
      <RuggedTopNav
        user={user}
        draftCount={routes.filter((r) => r.is_draft).length}
        errorCount={errorCount}
        onTabChange={(id) => {
          if (id === 'dashboard') return;
          if (id === 'upload') { setUploadOpen(true); return; }
          if (id === 'drafts') { setDraftsOpen(true); return; }
          if (id === 'errors') { setErrorsOpen(true); return; }
          if (id === 'ranking') { setRankingOpen(true); return; }
          if (id === 'gamification') { navigate('/autocolantes'); return; }
          if (id === 'empresas') { setCompaniesOpen(true); return; }
          if (id === 'municipios') { setMunicipiosOpen(true); return; }
          if (id === 'my-municipio') { navigate('/municipio'); return; }
          if (id === 'my-company') {
            navigate(['company_admin', 'company_staff'].includes(user?.role) ? '/empresa' : '/empresa/membro');
            return;
          }
          const map = {
            upload: null, // handled above via setUploadOpen
            users: 'open-users-manager-btn',
            reports: 'reports-moderation-button',
          };
          const t = map[id];
          if (t) {
            const el = document.querySelector(`[data-testid="${t}"]`);
            if (el && typeof el.click === 'function') el.click();
          }
        }}
      />
      {/* Painel Rascunhos (admin) — lista de rotas is_draft=true com opções
          de Publicar (+ push) ou Eliminar. */}
      <DraftsPanel
        open={draftsOpen}
        onClose={setDraftsOpen}
        onChange={() => {
          // Refresh routes para actualizar contador na topnav + lista visível
          axios.get(`${API}/routes`).then((r) => setRoutes(r.data || [])).catch(() => {});
        }}
      />
      {/* Error Monitor (admin) — erros backend/web/mobile num único painel. */}
      <ErrorConsole
        open={errorsOpen}
        onClose={setErrorsOpen}
        onChange={() => {
          // Refresh badge count após resolver erros
          axios.get(`${API}/admin/errors/stats`, { withCredentials: true })
            .then((r) => setErrorCount(r.data?.unresolved || 0))
            .catch(() => {});
        }}
      />
      {/* Ranking Card (admin) — pódio top-3 semanal/mensal para partilha. */}
      <RankingCardModal
        open={rankingOpen}
        onClose={setRankingOpen}
      />
      {/* Associar Empresa — modal disparado pela tab "Empresas" no top nav. */}
      <CompaniesManager
        open={companiesOpen}
        onOpenChange={setCompaniesOpen}
      />
      {/* Municípios — modal disparado pela tab "Municípios" no top nav (Fev/2026).
           Reutiliza `MunicipalitiesManager` mas em modo controlado (open/onOpenChange
           via props em vez do próprio DialogTrigger). */}
      <MunicipalitiesManager
        open={municipiosOpen}
        onOpenChange={setMunicipiosOpen}
      />
      {/* Social Cards (admin) — modal disparado do botão "📱 CARD" em cada rota. */}
      {socialCardsFor && (
        <SocialCardsModal
          open={!!socialCardsFor}
          onClose={() => setSocialCardsFor(null)}
          routeId={socialCardsFor.route_id}
          routeName={socialCardsFor.name}
        />
      )}
      {/* Associar rota a municípios (many-to-many). Botão 🏛️ em cada rota. */}
      {muniModalFor && (
        <RouteMunicipalitiesModal
          route={muniModalFor}
          open={!!muniModalFor}
          onClose={() => setMuniModalFor(null)}
        />
      )}
      {/* Terms & Conditions disclaimer — bloqueante até aceitar */}
      <TermsModal
        open={!!user && !termsAccepted}
        onAccept={acceptTerms}
        onLogout={logoutFromTerms}
      />
      {/* Enforcement da subscrição no web (idêntico ao mobile):
           granted (trial/PRO/OEM/exempt) → pill fino · grace → banner âmbar ·
           blocked → overlay bloqueante */}
      {termsAccepted && <WebBillingGuard />}
      {/* Offline banner */}
      {!isOnline && (
        <div
          className="w-full bg-amber-500 text-white text-sm font-semibold py-2 px-4 text-center shadow-md"
          data-testid="offline-banner"
        >
          ⚠️ Sem ligação à Internet — funcionalidades de download desativadas
        </div>
      )}
      <div className="flex-1 flex flex-col md:flex-row md:overflow-hidden min-h-0">
      {/* Sidebar — TODA a coluna scrolla como uma peça só. */}
      <aside className="w-full md:w-96 md:flex-shrink-0 bg-card border-r border-border flex flex-col min-h-0 md:overflow-y-auto">
        {/* Header (não fixa altura — scroll partilhado com routes) */}
        <div className="p-3 md:p-4 border-b border-border flex-shrink-0">
          {/* Linha 1: Brand row (mock-admin-dashboard-rugged.html) */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center min-w-0 gap-2.5">
              {/* Brand mark: círculo TC (mock) */}
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  background: '#E87A2A',
                  color: '#0F0D0B',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  fontWeight: 900,
                  border: '2px solid #F2EBDD',
                  letterSpacing: '0.5px',
                  flexShrink: 0,
                }}
                data-testid="tectur-logo-dashboard"
                title="Tectur 4x4"
              >
                TC
              </div>
              <div className="min-w-0">
                <h1
                  className="font-black tracking-widest truncate"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '14px',
                    letterSpacing: '2px',
                    color: '#F2EBDD',
                  }}
                >
                  TECTUR 4×4
                </h1>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '8px',
                    color: '#E87A2A',
                    letterSpacing: '2.5px',
                    marginTop: '2px',
                  }}
                >
                  OFF-ROAD · {user?.role === 'admin' ? 'ADMIN' : user?.role === 'staff' ? 'STAFF' : 'USER'}
                </div>
              </div>
            </div>
            {/* Avatar com badge ADMIN (mock) */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '2px solid #E87A2A',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #C4A275, #E87A2A)',
                    color: '#0F0D0B',
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 900,
                    fontSize: 12,
                    border: '2px solid #E87A2A',
                  }}
                >
                  {(user?.name || user?.email || 'U').substring(0, 2).toUpperCase()}
                </div>
              )}
              {(user?.role === 'admin' || user?.role === 'staff') && (
                <span
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -12,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 7,
                    fontWeight: 900,
                    letterSpacing: 1,
                    background: '#E87A2A',
                    color: '#0F0D0B',
                    padding: '1px 4px',
                    borderRadius: 2,
                  }}
                >
                  {user.role === 'admin' ? 'ADMIN' : 'STAFF'}
                </span>
              )}
            </div>
          </div>

          {/* Linha 2: Action buttons organizados em GRUPOS lógicos com
              separadores visuais (barrinha vertical entre grupos):

                Grupo 1 · Comunicação em tempo real  → SOS · Walkie · Broadcast
                Grupo 2 · Comunidade / Passeios       → Grupos · Passeios · Desafios
                Grupo 3 · Admin / Gestão              → Users · Reset · OEM · Reports · Metrics
                Grupo 4 · Pessoal                     → Histórico · Password · Tema · Sair

              Mobile/landscape fix: em qualquer ecrã sub-desktop (≤ lg / 1024px)
              a toolbar fica em UMA linha horizontal com scroll para não consumir
              altura vertical — indispensável em landscape phone (h ≈ 375px)
              onde antes o wrap escondia a lista de rotas.
              Só em desktop (lg+) permitimos wrap para várias linhas.
          */}
          <div
            className={`tectur-toolbar ${toolbarMore ? 'more-open' : ''} flex items-center gap-1 mb-3 flex-nowrap overflow-x-auto lg:flex-wrap lg:overflow-visible pb-1 lg:pb-0`}
            data-testid="dashboard-toolbar"
            style={{ scrollbarWidth: 'thin' }}
          >
            {/* Grupo 1 — Comunicação em tempo real (crítico/vermelho) */}
            <div className="flex items-center gap-1 flex-shrink-0" data-testid="toolbar-group-realtime">
              {/* SOS Live: TODOS vêem — backend filtra por membership do user */}
              <SOSLiveDashboard user={user} />
              {(user?.role === 'admin' || user?.role === 'staff') && <WebWalkie />}
              {(user?.role === 'admin' || user?.role === 'staff') && <BroadcastPanel />}
            </div>

            <span className="w-px h-5 bg-slate-300 mx-1 flex-shrink-0" aria-hidden="true" />

            {/* Grupo 2 — Comunidade & Passeios (azul/desafio) */}
            <div className="flex items-center gap-1 flex-shrink-0" data-testid="toolbar-group-community">
              {/* GroupsManager: TODOS os roles vêem — mas User/Staff só gerem grupos que criaram */}
              <GroupsManager currentUser={user} />
              {/* Passeios Organizados: TODOS os roles vêem — 1 botão, 2 abas (Agenda + Ao Vivo). */}
              <PasseiosOrganizados user={user} />
              <CommunityPanel
                user={user}
                onOpenRoute={(rid) => {
                  const r = routes.find((x) => x.route_id === rid);
                  if (r) setSelectedRoute(r);
                  else toast.error('Rota não disponível');
                }}
              />
              {user?.role === 'admin' && <NavChallengeManager />}
            </div>

            {(user?.role === 'admin' || user?.role === 'staff') && (
              <>
                <span className="w-px h-5 bg-slate-300 mx-1 flex-shrink-0" aria-hidden="true" />
                {/* Grupo 3 — Admin / Gestão (só admin+staff) */}
                <div className="flex items-center gap-1 flex-shrink-0" data-testid="toolbar-group-admin">
                  {user?.role === 'admin' && <UsersManager currentUserId={user.user_id} />}
                  {user?.role === 'admin' && <MunicipalitiesManager />}
                  {user?.role === 'admin' && <ResetRequestsManager />}
                  {user?.role === 'admin' && <OemKeysManager />}
                  {user?.role === 'admin' && <SupportMessagesPanel />}
                  <ReportsModerationPanel />
                  <MetricsPanel />
                </div>
              </>
            )}

            <span className="w-px h-5 bg-slate-300 mx-1 flex-shrink-0" aria-hidden="true" />

            {/* Grupo 4 — Pessoal (histórico, password, tema, sair) */}
            <div className="flex items-center gap-1 flex-shrink-0" data-testid="toolbar-group-personal">
              <HistoryViewer user={user} />
              <AutocolantesPanel user={user} />
              {/* Contacto — só para role user (admin/staff usam gestão direta) */}
              {user?.role === 'user' && <ContactForm />}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPwdOpen(true)}
                data-testid="change-password-button"
                title="Alterar palavra-passe"
              >
                <KeyRound className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button" title="Sair">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>

            {/* SOS popup é suprimido até o user aceitar os Termos & Condições
                — disclaimer legal deve ter prioridade absoluta sobre todos
                os outros modais. Não conta como botão visível — é overlay. */}
            <button
              type="button"
              className="toolbar-more-btn md:hidden"
              onClick={() => setToolbarMore((v) => !v)}
              data-testid="toolbar-more-btn"
            >
              {toolbarMore ? '✕ Fechar' : '⋯ Mais'}
            </button>
            {termsAccepted && <SOSAlertsBanner user={user} />}
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Pesquisar rotas..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-routes-input"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {/* Fev/2026 — Filtro Município (admin only) — quando escolhes um
                 concelho, a lista de rotas fica scopada apenas às rotas
                 atribuídas a esse município. "Todas" volta a esconder rotas
                 de município (comportamento default limpo). */}
            {user?.role === 'admin' && Object.keys(muniMap).length > 0 && (
              <div className="col-span-2 flex gap-2">
                <Select value={municipioFilter} onValueChange={setMunicipioFilter}>
                  <SelectTrigger data-testid="municipio-filter" className="flex-1">
                    <SelectValue placeholder="Município" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🌍 Todos (sem rotas de município)</SelectItem>
                    {Object.values(muniMap)
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((m) => (
                        <SelectItem key={m.municipality_id} value={m.municipality_id}>
                          🏛️ {m.name}{m.district ? ` · ${m.district}` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {municipioFilter !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setMunicipioFilter('all')}
                    className="px-3 rounded-md border border-input bg-background text-xs font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-900 whitespace-nowrap"
                    title="Limpar filtro de município e voltar à lista global"
                    data-testid="municipio-filter-clear"
                  >
                    ✕ Limpar
                  </button>
                )}
              </div>
            )}
            {user?.role !== 'admin' && ((user?.role === 'municipio' && user?.municipality_id) || user?.company_id) && (
              <button
                type="button"
                onClick={() => setOnlyMine((v) => !v)}
                className={`col-span-2 px-3 py-2 rounded-md border text-xs font-bold text-left transition-colors ${onlyMine ? 'border-primary bg-primary/15 text-primary' : 'border-input bg-background text-muted-foreground hover:text-foreground'}`}
                data-testid="only-mine-filter"
                aria-pressed={onlyMine}
              >
                {onlyMine ? '✓ ' : ''}
                {user?.role === 'municipio'
                  ? `🏛️ Só rotas de ${muniMap[user.municipality_id]?.name || 'o meu município'}`
                  : `🏢 Só rotas de ${companyName || 'a minha empresa'}`}
              </button>
            )}
            <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
              <SelectTrigger data-testid="difficulty-filter">
                <SelectValue placeholder="Dificuldade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="easy">Fácil</SelectItem>
                <SelectItem value="moderate">Moderada</SelectItem>
                <SelectItem value="hard">Difícil</SelectItem>
                <SelectItem value="expert">Especialista</SelectItem>
              </SelectContent>
            </Select>

            {/* Categoria — multi-select (podem estar várias marcadas em simultâneo) */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="type-filter"
                  className="flex items-center justify-between h-10 px-3 rounded-md border border-input bg-background text-sm text-left"
                >
                  <span className="truncate">
                    {typeFilter.length === 0
                      ? 'Categoria'
                      : typeFilter.map((v) => ({
                          'suv-4x4': '🚙 SUV 4x4',
                          '4x4-reductoras': '🚜 4x4 Redutoras',
                          car: '🚗 Carro',
                          moto: '🏍️ Mota',
                        }[v])).join(', ')}
                  </span>
                  <span className="ml-2 text-slate-400">▾</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                {[
                  { id: 'suv-4x4', label: '🚙 SUV 4x4' },
                  { id: '4x4-reductoras', label: '🚜 4x4 Redutoras' },
                  { id: 'car', label: '🚗 Carro' },
                  { id: 'moto', label: '🏍️ Mota' },
                ].map((opt) => {
                  const checked = typeFilter.includes(opt.id);
                  return (
                    <label
                      key={opt.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-sm"
                      data-testid={`type-filter-opt-${opt.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const cur = new Set(typeFilter);
                          if (v) cur.add(opt.id); else cur.delete(opt.id);
                          setTypeFilter(Array.from(cur));
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
                {typeFilter.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTypeFilter([])}
                    className="w-full mt-1 text-xs text-slate-500 hover:text-slate-900 py-1"
                    data-testid="type-filter-clear"
                  >
                    Limpar seleção
                  </button>
                )}
              </PopoverContent>
            </Popover>
          </div>

          {/* Upload button (admin only) */}
          {user?.role === 'admin' && (
            <UploadRouteDialog
              open={uploadOpen}
              onOpenChange={(open) => {
                setUploadOpen(open);
                if (!open) setTurnsStatus(null); // reset status quando fecha
                if (open && availableGroupsForEdit.length === 0) {
                  // Carrega lista de grupos para dropdown "Restringir a Grupo"
                  axios.get(`${API}/groups`, { withCredentials: true })
                    .then((res) => setAvailableGroupsForEdit(res.data || []))
                    .catch(() => {});
                }
                if (open && availableCompaniesForUpload.length === 0) {
                  axios.get(`${API}/companies`, { withCredentials: true })
                    .then((res) => setAvailableCompaniesForUpload(res.data?.companies || res.data || []))
                    .catch(() => {});
                }
                if (open && availableMunisForUpload.length === 0) {
                  axios.get(`${API}/municipalities`, { withCredentials: true })
                    .then((res) => setAvailableMunisForUpload(res.data || []))
                    .catch(() => {});
                }
              }}
              data={uploadData}
              setData={setUploadData}
              uploading={uploading}
              onSubmit={handleUpload}
              availableGroups={availableGroupsForEdit}
              availableCompanies={availableCompaniesForUpload}
              availableMunicipalities={availableMunisForUpload}
              turnsStatus={turnsStatus}
            />
          )}

          {/* Edit Route Dialog (admin) */}
          {user?.role === 'admin' && (
            <EditRouteDialog
              open={editOpen}
              onOpenChange={setEditOpen}
              data={editData}
              setData={setEditData}
              editSaving={editSaving}
              onSubmit={handleEditSave}
              availableGroups={availableGroupsForEdit}
              reloadFile={reloadFile}
              setReloadFile={setReloadFile}
              reloadSaving={reloadSaving}
              onReloadContent={handleReloadContent}
              patchRoute={patchRoute}
              api={API}
            />
          )}

          {/* Fev/2026 (v20) — Expandir POIs / Fotos / Avaliações em janela grande */}
          <Dialog open={!!expandedPanel} onOpenChange={(o) => { if (!o) setExpandedPanel(null); }}>
            <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="expanded-panel-dialog">
              <DialogHeader>
                <DialogTitle>
                  {expandedPanel === 'pois' && <>📍 POIs — {selectedRoute?.name || 'Rota'}</>}
                  {expandedPanel === 'photos' && <>🖼️ Fotos — {selectedRoute?.name || 'Rota'}</>}
                  {expandedPanel === 'ratings' && <>⭐ Avaliações — {selectedRoute?.name || 'Rota'}</>}
                </DialogTitle>
                <DialogDescription>
                  {expandedPanel === 'pois'
                    ? 'Edita, adiciona ou reordena todos os pontos de interesse desta rota.'
                    : expandedPanel === 'photos'
                    ? 'Vista ampliada da galeria completa.'
                    : 'Vista ampliada com todas as avaliações e comentários.'}
                </DialogDescription>
              </DialogHeader>
              {expandedPanel === 'pois' && selectedRoute && (
                <POIManager
                  route={selectedRoute}
                  isAdmin={user?.role === 'admin'}
                  onPOIsUpdated={refreshSelectedRoute}
                  onPOIFocus={(poi) => setFocusPOI({ ...poi, _t: Date.now() })}
                />
              )}
              {expandedPanel === 'photos' && selectedRoute && (selectedRoute.photo_count || 0) > 0 && (
                <RoutePhotosGallery
                  routeId={selectedRoute.route_id}
                  count={selectedRoute.photo_count}
                  apiUrl={API}
                  canDelete={user?.role === 'admin'}
                  onDelete={async (idx) => {
                    try {
                      await axios.delete(`${API}/routes/${selectedRoute.route_id}/photos/${idx}`, { withCredentials: true });
                      toast.success('Foto removida');
                      await refreshSelectedRoute();
                    } catch (e) {
                      toast.error('Falha a remover foto');
                    }
                  }}
                />
              )}
              {expandedPanel === 'ratings' && selectedRoute && (
                <RouteRatings route={selectedRoute} user={user} />
              )}
            </DialogContent>
          </Dialog>

          {/* Change Password Dialog (any authenticated user) */}
          <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
            <DialogContent data-testid="password-dialog">
              <DialogHeader>
                <DialogTitle>Alterar Palavra-Passe</DialogTitle>
                <DialogDescription>Mínimo 8 caracteres. Recomendamos misturar letras, números e símbolos.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handlePasswordSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pwd-current">Palavra-passe atual *</Label>
                  <Input
                    id="pwd-current"
                    type="password"
                    autoComplete="current-password"
                    value={pwdData.current}
                    onChange={(e) => setPwdData({ ...pwdData, current: e.target.value })}
                    required
                    data-testid="pwd-current-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pwd-new">Nova palavra-passe *</Label>
                  <Input
                    id="pwd-new"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={pwdData.next}
                    onChange={(e) => setPwdData({ ...pwdData, next: e.target.value })}
                    required
                    data-testid="pwd-new-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pwd-confirm">Confirmar nova palavra-passe *</Label>
                  <Input
                    id="pwd-confirm"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={pwdData.confirm}
                    onChange={(e) => setPwdData({ ...pwdData, confirm: e.target.value })}
                    required
                    data-testid="pwd-confirm-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={pwdSaving} data-testid="pwd-submit-button">
                  {pwdSaving ? 'A guardar...' : 'Alterar palavra-passe'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Rota do Mês (votação) */}
        <RotaDoMesWeb
          routes={routes}
          onOpenRoute={(rid) => {
            const r = routes.find((x) => x.route_id === rid);
            if (r) setSelectedRoute(r);
          }}
        />

        {/* Routes list — sem ScrollArea (a aside inteira já faz scroll) */}
        <div className="p-4 space-y-3">
          {filteredRoutes.length === 0 ? (
            <p className="text-center text-muted-foreground py-8" data-testid="no-routes-message">
              Nenhuma rota encontrada
            </p>
          ) : (
              filteredRoutes.map((route) => {
                const isPrivate = typeof route.is_private === 'boolean'
                  ? route.is_private
                  : (route.group_ids?.length || 0) > 0;
                const difficultyLabel = {
                  easy: 'FÁCIL',
                  moderate: 'MODERADA',
                  hard: 'DIFÍCIL',
                  expert: 'EXPERT',
                }[route.difficulty] || null;
                const isSelected = selectedRoute?.route_id === route.route_id;
                return (
                  <article
                    key={route.route_id}
                    className={`route-card-rugged ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedRoute(route);
                      setFocusPOI(null);
                      if (window.innerWidth < 768) {
                        setTimeout(() => document.querySelector('[data-testid="map-container"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                      }
                    }}
                    data-testid={`route-card-${route.route_id}`}
                  >
                    {/* COVER — 88px altura, badges no canto, logos empilhados */}
                    <div
                      className="route-cover-rugged"
                      style={{
                        height: 88,
                        position: 'relative',
                        background: route.cover_image_path
                          ? `linear-gradient(rgba(15,13,11,.35), rgba(15,13,11,.75)), url(${API}/routes/${route.route_id}/cover) center/cover`
                          : 'linear-gradient(135deg, #3D362E, #1A1613)',
                      }}
                    >
                      {/* Badges: PÚBLICA/PRIVADA + DIFICULDADE */}
                      <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 3 }}>
                        <span
                          className={`badge-mono ${isPrivate ? 'private' : 'public'}`}
                          data-testid={`route-visibility-badge-${route.route_id}`}
                          title={isPrivate ? 'Rota privada — só membros dos grupos' : 'Rota pública'}
                        >
                          {isPrivate ? '🔒 PRIVADA' : '🌍 PÚBLICA'}
                        </span>
                        {difficultyLabel && (
                          <span className="badge-mono">
                            {difficultyLabel}
                          </span>
                        )}
                      </div>
                      {/* Logos empilhados canto inferior direito */}
                      {(route.logo_count || 0) > 0 && (
                        <div
                          style={{ position: 'absolute', bottom: 6, right: 6, display: 'flex' }}
                          data-testid={`route-logos-${route.route_id}`}
                        >
                          {Array.from({ length: Math.min(route.logo_count, 3) }).map((_, i) => (
                            <img
                              key={i}
                              src={`${API}/routes/${route.route_id}/logos/${i}`}
                              alt=""
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                border: '2px solid #0F0D0B',
                                objectFit: 'cover',
                                marginLeft: i === 0 ? 0 : -6,
                                zIndex: 10 - i,
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* BODY */}
                    <div style={{ padding: '10px 12px' }}>
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: 13,
                          color: '#F2EBDD',
                          marginBottom: 4,
                          letterSpacing: '0.2px',
                        }}
                      >
                        {route.name}
                      </div>
                      {route.region && (
                        <div
                          data-testid={`route-region-${route.route_id}`}
                          style={{
                            fontSize: 10,
                            color: 'rgba(242,235,221,.55)',
                            marginBottom: 6,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontFamily: "'JetBrains Mono', monospace",
                            letterSpacing: '0.5px',
                          }}
                        >
                          <span style={{ color: '#C4A275' }}>◈</span>
                          {route.region}
                        </div>
                      )}
                      {Array.isArray(route.tags) && route.tags.length > 0 && (
                        <div
                          style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}
                          data-testid={`route-tags-${route.route_id}`}
                        >
                          {route.tags.slice(0, 5).map((t) => (
                            <span
                              key={t}
                              style={{
                                fontSize: 9,
                                padding: '2px 6px',
                                borderRadius: 3,
                                background: 'rgba(196,162,117,.10)',
                                color: '#C4A275',
                                fontFamily: "'JetBrains Mono', monospace",
                              }}
                            >
                              #{t}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Municípios associados (pills indigo) — só admin */}
                      {user?.role === 'admin' && Array.isArray(route.municipality_ids) && route.municipality_ids.length > 0 && (
                        <div
                          style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}
                          data-testid={`route-municipalities-pills-${route.route_id}`}
                          onClick={(e) => { e.stopPropagation(); setMuniModalFor(route); }}
                          title="Clica para editar municípios"
                        >
                          {route.municipality_ids.slice(0, 4).map((mid) => {
                            const m = muniMap[mid];
                            const logoSrc = m?.has_logo
                              ? `${API}/municipalities/${mid}/logo`
                              : null;
                            return (
                              <span
                                key={mid}
                                style={{
                                  fontSize: 9,
                                  padding: '2px 6px 2px 4px',
                                  borderRadius: 999,
                                  background: 'rgba(129,140,248,.18)',
                                  color: '#A5B4FC',
                                  fontFamily: "'JetBrains Mono', monospace",
                                  fontWeight: 700,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  cursor: 'pointer',
                                }}
                                data-testid={`route-muni-pill-${route.route_id}-${mid}`}
                              >
                                {logoSrc ? (
                                  <img
                                    src={logoSrc}
                                    alt=""
                                    style={{
                                      width: 14,
                                      height: 14,
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      background: '#0F0D0B',
                                      border: '1px solid rgba(129,140,248,.35)',
                                    }}
                                    onError={(e) => {
                                      // Fallback silencioso para o ícone se
                                      // o brasão falhar a carregar (rede/404).
                                      e.currentTarget.style.display = 'none';
                                      const fb = e.currentTarget.nextSibling;
                                      if (fb) fb.style.display = 'inline-flex';
                                    }}
                                    data-testid={`route-muni-logo-${mid}`}
                                  />
                                ) : null}
                                <Building2
                                  size={11}
                                  style={{ color: '#818CF8', display: logoSrc ? 'none' : 'inline-flex' }}
                                />
                                {m?.name || mid}
                              </span>
                            );
                          })}
                          {route.municipality_ids.length > 4 && (
                            <span
                              style={{
                                fontSize: 9,
                                padding: '2px 6px',
                                borderRadius: 999,
                                background: 'rgba(129,140,248,.10)',
                                color: '#A5B4FC',
                                fontFamily: "'JetBrains Mono', monospace",
                                fontWeight: 700,
                              }}
                            >
                              +{route.municipality_ids.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Stats inline: 📏 12.4 km · ⛰️ +820 m · ⏱ 2h15 · ⭐ 4.8 */}
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          fontSize: 10,
                          color: 'rgba(242,235,221,.55)',
                          marginBottom: 6,
                          flexWrap: 'wrap',
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {route.distance_km && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            📏 <b style={{ color: '#F2EBDD', fontWeight: 900 }}>{route.distance_km} km</b>
                          </span>
                        )}
                        {route.elevation_gain_m && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            ⛰️ <b style={{ color: '#F2EBDD', fontWeight: 900 }}>+{route.elevation_gain_m} m</b>
                          </span>
                        )}
                        {route.duration_hours && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            ⏱ <b style={{ color: '#F2EBDD', fontWeight: 900 }}>{route.duration_hours}h</b>
                          </span>
                        )}
                        {route.avg_rating && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            ⭐ <b style={{ color: '#F2EBDD', fontWeight: 900 }}>{Number(route.avg_rating).toFixed(1)}</b>
                          </span>
                        )}
                      </div>
                      {/* Vehicle badges pequenas (SUV / carro / mota) */}
                      {(route.route_type || (route.vehicle_badges || []).length > 0) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                          {route.route_type === 'suv-4x4' && (
                            <span className="badge-mono" style={{ color: '#38BDF8', borderColor: 'rgba(56,189,248,.4)' }}>🚙 SUV 4×4</span>
                          )}
                          {route.route_type === '4x4-reductoras' && (
                            <span className="badge-mono" style={{ color: '#FACC15', borderColor: 'rgba(250,204,21,.4)' }}>🚜 REDUTORAS</span>
                          )}
                          {(route.vehicle_badges || []).includes('car') && (
                            <span className="badge-mono" data-testid={`badge-car-${route.route_id}`}>🚗 CARRO</span>
                          )}
                          {(route.vehicle_badges || []).includes('moto') && (
                            <span className="badge-mono" data-testid={`badge-moto-${route.route_id}`}>🏍️ MOTA</span>
                          )}
                          {isInMyDownloads(route.route_id) && (
                            <span
                              className="badge-mono"
                              style={{ color: '#22C55E', borderColor: 'rgba(34,197,94,.4)' }}
                              data-testid={`badge-in-my-downloads-${route.route_id}`}
                            >
                              📲 NO TEL
                            </span>
                          )}
                        </div>
                      )}
                      {/* Actions: pill-shaped, mono, hover terracota */}
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 4,
                          paddingTop: 6,
                          borderTop: '1px solid rgba(196,162,117,.15)',
                        }}
                      >
                        {isInMyDownloads(route.route_id) ? (
                          <button
                            className="route-action-rugged"
                            style={{ color: '#22C55E' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFromMyDownloads(route);
                            }}
                            data-testid={`remove-my-download-${route.route_id}`}
                            title="Remover do meu telemóvel"
                          >
                            ✓ NO MEU TEL
                          </button>
                        ) : (
                          <button
                            className="route-action-rugged"
                            disabled={!isOnline}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToMyDownloads(route);
                            }}
                            data-testid={`add-my-download-${route.route_id}`}
                            title="Guardar rota no meu telemóvel"
                          >
                            📲 NO MEU TLM
                          </button>
                        )}
                        <button
                          className="route-action-rugged share"
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = `${BACKEND_URL}/api/share/${route.route_id}`;
                            navigator.clipboard.writeText(url);
                            toast.success('Link público copiado!', { description: url });
                          }}
                          data-testid={`share-route-${route.route_id}`}
                          title="Partilhar link público"
                        >
                          ↗ PARTILHAR
                        </button>
                        {user?.role === 'admin' && (
                          <>
                            <button
                              className="route-action-rugged edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(route);
                              }}
                              data-testid={`edit-route-${route.route_id}`}
                              title="Editar rota"
                            >
                              ✎ EDITAR
                            </button>
                            <button
                              className="route-action-rugged social"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSocialCardsFor(route);
                              }}
                              data-testid={`social-card-route-${route.route_id}`}
                              title="Gerar cards para redes sociais"
                            >
                              📱 CARD
                            </button>
                            <button
                              className="route-action-rugged social"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMuniModalFor(route);
                              }}
                              data-testid={`route-municipalities-${route.route_id}`}
                              title="Associar rota a municípios"
                            >
                              <Building2 className="inline-block w-3 h-3 mr-1" style={{verticalAlign: '-2px'}} /> MUNICÍPIOS
                            </button>
                            <button
                              className="route-action-rugged delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(route.route_id);
                              }}
                              data-testid={`delete-route-${route.route_id}`}
                              title="Eliminar rota"
                            >
                              ✕ ELIMINAR
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })
          )}
        </div>
      </aside>

      {/* Map and Elevation */}
      <div className="md:flex-1 min-w-0 relative isolate flex flex-col md:overflow-hidden scroll-mt-2" data-testid="map-container">
        {selectedRoute ? (
          <>
            {/* Botão "Iniciar Navegação" removido do web (Ago/2026): GPS
                turn-by-turn não faz sentido em desktop; vive só no mobile. */}
            <div className="h-[60vh] md:h-auto md:flex-1 md:min-h-[240px]">
              <RouteMap route={selectedRoute} focusPOI={focusPOI} />
            </div>
            <div className="border-t border-border bg-background flex-shrink-0" data-testid="bottom-panel">
              <Tabs defaultValue="elevation">
                <TabsList className="w-full justify-start rounded-none border-b bg-muted/50 px-2 h-10">
                  {selectedRoute.elevation_profile && selectedRoute.elevation_profile.length > 0 && (
                    <TabsTrigger value="elevation" data-testid="tab-elevation">
                      📈 Elevação
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="ratings" data-testid="tab-ratings">
                    ⭐ Avaliações
                  </TabsTrigger>
                  <TabsTrigger value="pois" data-testid="tab-pois">
                    📍 POIs {(selectedRoute.pois?.length || 0) > 0 ? `(${selectedRoute.pois.length})` : ''}
                  </TabsTrigger>
                  {(selectedRoute.photo_count || 0) > 0 && (
                    <TabsTrigger value="photos" data-testid="tab-photos">
                      🖼️ Fotos ({selectedRoute.photo_count})
                    </TabsTrigger>
                  )}
                </TabsList>
                {selectedRoute.elevation_profile && selectedRoute.elevation_profile.length > 0 && (
                  <TabsContent value="elevation" className="p-4 md:max-h-[30vh] md:overflow-y-auto m-0">
                    <ElevationChart
                      elevationProfile={selectedRoute.elevation_profile}
                      distance={selectedRoute.distance_km}
                      elevationGain={selectedRoute.elevation_gain_m}
                    />
                  </TabsContent>
                )}
                <TabsContent value="ratings" className="p-4 md:max-h-[30vh] md:overflow-y-auto m-0" data-testid="ratings-panel">
                  <div className="flex justify-end mb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setExpandedPanel('ratings')}
                      data-testid="expand-ratings-btn"
                      className="h-7 text-xs"
                    >
                      <Maximize2 className="h-3.5 w-3.5 mr-1" /> Expandir
                    </Button>
                  </div>
                  <RouteRatings route={selectedRoute} user={user} />
                </TabsContent>
                <TabsContent value="pois" className="p-4 md:max-h-[30vh] md:overflow-y-auto m-0" data-testid="pois-panel">
                  <div className="flex justify-end mb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setExpandedPanel('pois')}
                      data-testid="expand-pois-btn"
                      className="h-7 text-xs"
                    >
                      <Maximize2 className="h-3.5 w-3.5 mr-1" /> Expandir
                    </Button>
                  </div>
                  <POIManager
                    route={selectedRoute}
                    isAdmin={user?.role === 'admin'}
                    onPOIsUpdated={refreshSelectedRoute}
                    onPOIFocus={(poi) => setFocusPOI({ ...poi, _t: Date.now() })}
                  />
                </TabsContent>
                {(selectedRoute.photo_count || 0) > 0 && (
                  <TabsContent value="photos" className="p-4 md:max-h-[30vh] md:overflow-y-auto m-0" data-testid="photos-panel">
                    <div className="flex justify-end mb-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedPanel('photos')}
                        data-testid="expand-photos-btn"
                        className="h-7 text-xs"
                      >
                        <Maximize2 className="h-3.5 w-3.5 mr-1" /> Expandir
                      </Button>
                    </div>
                    <RoutePhotosGallery
                      routeId={selectedRoute.route_id}
                      count={selectedRoute.photo_count}
                      apiUrl={API}
                      canDelete={user?.role === 'admin'}
                      onDelete={async (idx) => {
                        try {
                          await axios.delete(`${API}/routes/${selectedRoute.route_id}/photos/${idx}`, { withCredentials: true });
                          toast.success('Foto removida');
                          await refreshSelectedRoute();
                        } catch (e) {
                          toast.error('Falha a remover foto');
                        }
                      }}
                    />
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </>
        ) : (
          <div className="h-[50vh] md:h-full w-full relative" data-testid="empty-state-map">
            <MapContainer
              center={[39.5, -8.0]}
              zoom={7}
              scrollWheelZoom={true}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | <a href="https://opentopomap.org">OpenTopoMap</a>'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                maxZoom={17}
              />
            </MapContainer>
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-card/95 backdrop-blur px-4 py-2 rounded-lg shadow-md z-[400] flex items-center gap-2 pointer-events-none">
              <Mountain className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground md:whitespace-nowrap">Selecione uma rota da lista para a visualizar</p>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
    </Suspense>
  );
};

export default Dashboard;
